import React from 'react';
import { useForm } from 'react-hook-form';

import FiltersBar from '@/components/FiltersBar';
import { appointmentAttendanceStateLabels } from '@/constants/appointmentAttendance';
import useFilterParams, { type FilterFields, type FilterValues } from '@/hooks/useFilterParams';
import Accordion from '@/ui/components/Accordion';
import ActiveFilters from '@/ui/components/ActiveFilters';
import CheckboxFilterGroup from '@/ui/components/CheckboxFilterGroup';
import FilterApplyButton from '@/ui/components/FilterApplyButton';

import { Props } from './types';

const APPOINTMENT_TYPE_GROUP = 'Appointment Type';
const STATE_FILTER = 'state__in';
const STATIC_PARAMS = { inclusive_appointment_type_fields: 'true' };

const APPOINTMENT_TYPE_FILTERS = {
  appointment__appointment_type__clinical: 'Clinical',
  appointment__appointment_type__counselor_1_vs_1: 'Counselor 1 vs 1',
  appointment__appointment_type__drug_screen_expected: 'Drug Screen Expected',
  appointment__appointment_type__reward_as_group_meeting: 'Reward as Group Meeting',
  appointment__appointment_type__initial_counselor_1_vs_1: 'Initial Counselor 1 vs 1',
  appointment__appointment_type__is_continued_intake: 'Continued Intake',
  appointment__appointment_type__is_initial_medical_check: 'Initial Medical Check',
  appointment__appointment_type__is_intake: 'Intake',
  appointment__appointment_type__peer_support: 'Peer Support',
  appointment__appointment_type__is_medical_check: 'Medical Check',
  appointment__program_adherence: 'Program Adherence'
};

const FILTER_FIELDS: FilterFields = {
  [STATE_FILTER]: { kind: 'multi', label: 'Status', options: appointmentAttendanceStateLabels },
  ...Object.fromEntries(
    Object.entries(APPOINTMENT_TYPE_FILTERS).map(([name, label]) => [
      name,
      { group: APPOINTMENT_TYPE_GROUP, kind: 'flag', label }
    ])
  )
};

const STATE_OPTIONS = Object.entries(appointmentAttendanceStateLabels).map(([value, label]) => ({
  label,
  name: STATE_FILTER,
  value
}));

const APPOINTMENT_TYPE_OPTIONS = Object.entries(APPOINTMENT_TYPE_FILTERS).map(([name, label]) => ({
  label,
  name
}));

const AppointmentsFilterForm = ({ activeParams, setParams }: Props) => {
  const { activeGroups, apply, clear, removeValue, values } = useFilterParams({
    fields: FILTER_FIELDS,
    searchParams: activeParams,
    setSearchParams: setParams,
    staticParams: STATIC_PARAMS
  });

  const {
    formState: { isSubmitting },
    handleSubmit,
    register
  } = useForm<FilterValues>({ values });

  return (
    <form className="relative" onSubmit={handleSubmit(apply)}>
      <FiltersBar
        filterSets={
          <div className="flex flex-col gap-1">
            <Accordion buttonText="Status">
              <CheckboxFilterGroup
                idPrefix="state-filter"
                options={STATE_OPTIONS}
                register={register}
              />
              <FilterApplyButton isSubmitting={isSubmitting} />
            </Accordion>
            <Accordion buttonText="Appointment Types">
              <CheckboxFilterGroup
                idPrefix="boolean-filter"
                options={APPOINTMENT_TYPE_OPTIONS}
                register={register}
              />
              <FilterApplyButton isSubmitting={isSubmitting} />
            </Accordion>
          </div>
        }>
        <ActiveFilters groups={activeGroups} onClearAll={clear} onRemove={removeValue} />
      </FiltersBar>
    </form>
  );
};
export default React.memo(AppointmentsFilterForm);
